import React, { useState } from "react";
import { useTranslation } from "react-i18next";

// A password input you can read back. Every password field on the site was a
// bare type="password", so nobody could ever check what they had typed — not at
// signup, where a typo creates an account with a value its owner never knew,
// and not at login, where they are guessing against it.
//
// One component rather than a toggle copied four times: the reveal state, the
// aria-label and the padding that keeps text clear of the button all have to
// agree, and they only agree if they live in one place.
export default function PasswordField({ id, value, onChange, autoComplete, invalid = false, ...rest }) {
  const [shown, setShown] = useState(false);
  const { t } = useTranslation("auth");

  return (
    <div className="relative">
      <input
        id={id}
        type={shown ? "text" : "password"}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        // pr-12 so a long password scrolls behind the button instead of under it.
        className={`w-full bg-[#1a1a1e] border rounded px-4 py-3 pr-12 text-white focus:outline-none transition-colors ${
          invalid ? "border-red-500/60 focus:border-red-500" : "border-white/10 focus:border-[#FF5500]"
        }`}
        {...rest}
      />
      <button
        type="button"
        // Reachable by Tab. It was tabIndex -1 on the theory that Tab should not
        // detour through a control that changes nothing about the value — but it
        // does change something, the visibility, and a keyboard-only user is
        // exactly who the reveal helps most. Skipping it was a WCAG 2.1.1 fail.
        onClick={() => setShown((s) => !s)}
        // aria-label alone, not aria-label + aria-pressed: together a screen
        // reader announces "Hide password, pressed", which says it twice.
        aria-label={shown ? t("password.hide") : t("password.show")}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition-colors"
      >
        {shown ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M3 3l18 18" strokeLinecap="round" />
            <path d="M10.6 5.1A9.6 9.6 0 0112 5c5 0 9 4.5 9 7 0 .8-.5 1.9-1.4 3M6.5 6.9C4.2 8.4 3 10.4 3 12c0 2.5 4 7 9 7 1.6 0 3-.4 4.3-1.1" strokeLinecap="round" />
            <path d="M9.9 9.9a3 3 0 004.2 4.2" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" strokeLinecap="round" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}
